import React, { useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputStyle = { width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

// Edits the member's single application record. Same record the Application form wrote.
const AppFieldsEditor = ({ section, application, onSaved, getAuthConfig, endpoint = '/applications/me' }) => {
  const [saving, setSaving] = useState('');
  const [savedKey, setSavedKey] = useState('');
  const [error, setError] = useState('');

  const save = async (key, value) => {
    setSaving(key); setError('');
    try {
      const { data } = await axios.patch(`${API}${endpoint}`, { [key]: value }, getAuthConfig());
      onSaved(data);
      setSavedKey(key);
      setTimeout(() => setSavedKey(''), 1500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save that change.');
    }
    setSaving('');
  };

  const toggleMulti = (key, option) => {
    const current = Array.isArray(application?.[key]) ? application[key] : (application?.[key] ? [application[key]] : []);
    const next = current.includes(option) ? current.filter(v => v !== option) : [...current, option];
    save(key, next);
  };

  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} data-testid={`app-section-${section.key}`}>
      <h3 style={{ margin: '0 0 6px', color: '#1a2332' }}>{section.title}</h3>
      {section.note && <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 18px' }}>{section.note}</p>}
      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', color: '#b91c1c', marginBottom: '14px', fontSize: '13px' }} data-testid="app-field-error">{error}</div>}

      {section.fields.map(field => {
        const value = application?.[field.key];
        const saved = savedKey === field.key;
        return (
          <div key={field.key} style={{ marginBottom: '18px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#1a2332' }}>
              {field.label}
              {saving === field.key && <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: '8px', fontSize: '12px' }}>Saving…</span>}
              {saved && <span style={{ color: '#059669', fontWeight: 600, marginLeft: '8px', fontSize: '12px' }}>Saved</span>}
            </label>

            {field.type === 'textarea' && (
              <textarea defaultValue={value || ''} rows={3} style={{ ...inputStyle, resize: 'vertical' }}
                onBlur={(e) => { if ((value || '') !== e.target.value) save(field.key, e.target.value); }}
                data-testid={`app-field-${field.key}`} />
            )}

            {(field.type === 'text' || field.type === 'number') && (
              <input type={field.type === 'number' ? 'number' : 'text'} defaultValue={value ?? ''} style={inputStyle}
                onBlur={(e) => { if (String(value ?? '') !== e.target.value) save(field.key, e.target.value); }}
                data-testid={`app-field-${field.key}`} />
            )}

            {field.type === 'select' && (
              <select value={value || ''} style={inputStyle}
                onChange={(e) => save(field.key, e.target.value)}
                data-testid={`app-field-${field.key}`}>
                <option value="">Not specified</option>
                {field.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}

            {field.type === 'bool' && (
              <div style={{ display: 'flex', gap: '18px' }} data-testid={`app-field-${field.key}`}>
                {[['Yes', true], ['No', false]].map(([lbl, val]) => (
                  <label key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                    <input type="radio" checked={value === val} onChange={() => save(field.key, val)} /> {lbl}
                  </label>
                ))}
              </div>
            )}

            {field.type === 'multi' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px' }} data-testid={`app-field-${field.key}`}>
                {field.options.map(([v, l]) => {
                  const arr = Array.isArray(value) ? value : (value ? [value] : []);
                  return (
                    <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', color: '#374151' }}>
                      <input type="checkbox" checked={arr.includes(v)} onChange={() => toggleMulti(field.key, v)} />
                      <span>{l}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>Changes save automatically. These are the same answers as your HSS Application — there is only one copy.</p>
    </div>
  );
};

export default AppFieldsEditor;
